/**
 * Estados vacíos, de carga y de error para listas y detalles. No existen como
 * componentes genéricos en punto-world (cada pantalla los pinta a mano), así que
 * están aquí, sobre las primitivas y los tokens copiados.
 */
import { AlertCircle, Inbox, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@punto-firma/ui/components/button";
import { Skeleton } from "@punto-firma/ui/components/skeleton";
import { cn } from "@punto-firma/ui/lib/utils";

export const EmptyState = ({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
}: {
  icon?: LucideIcon;
  title?: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) => {
  const { t } = useTranslation("common");
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed px-6 py-12 text-center",
        className,
      )}
    >
      <div className="flex size-10 items-center justify-center rounded-full bg-muted">
        <Icon className="size-5 text-muted-foreground" aria-hidden />
      </div>
      <div className="space-y-1">
        <p className="font-medium">{title ?? t("empty")}</p>
        {description ? (
          <p className="max-w-sm text-sm text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      {action}
    </div>
  );
};

/** Esqueleto de una lista mientras carga. */
export const LoadingState = ({
  rows = 4,
  className,
}: {
  rows?: number;
  className?: string;
}) => {
  const { t } = useTranslation("common");
  return (
    <div
      className={cn("space-y-3", className)}
      role="status"
      aria-live="polite"
      aria-label={t("loading")}
    >
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-10 w-full" />
      ))}
    </div>
  );
};

export const ErrorState = ({
  title,
  description,
  onRetry,
  className,
}: {
  title?: string;
  description?: React.ReactNode;
  onRetry?: () => void;
  className?: string;
}) => {
  const { t } = useTranslation(["errors", "common"]);
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-lg border border-destructive-base bg-destructive-light px-6 py-12 text-center text-destructive-light-foreground",
        className,
      )}
    >
      <AlertCircle className="size-6 text-destructive" aria-hidden />
      <div className="space-y-1">
        <p className="font-medium">{title ?? t("errors:unexpectedTitle")}</p>
        <p className="max-w-sm text-sm">
          {description ?? t("errors:unexpectedDescription")}
        </p>
      </div>
      {onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t("common:retry")}
        </Button>
      ) : null}
    </div>
  );
};
